/**
 * Move validation, word extraction and scoring — the heart of the rules.
 * Pure and shared: the client uses it (without a dictionary) for the live
 * score preview; the server uses it (with the dictionary) to accept moves.
 */
import { inBounds, isBoardEmpty, premiumAt, tileAt } from './board';
import { BINGO_BONUS, BOARD_SIZE, CENTER, PREMIUM_MULTIPLIER, RACK_SIZE } from './constants';
import type { Dictionary } from './dictionary';
import { messages } from './messages';
import type {
  Board,
  Coord,
  Direction,
  FormedWord,
  MoveError,
  MoveEvaluation,
  Placement,
  Tile,
  WordCell,
} from './types';

// ---------------------------------------------------------------------------
// Scoring
// ---------------------------------------------------------------------------

/**
 * Score one word: letter premiums (DL/TL) and word premiums (DW/TW) apply ONLY
 * under newly placed tiles; blanks are 0; word multipliers compound.
 *
 *   letterSum      = Σ face × (isNew ? letterMultiplier(premium) : 1)
 *   wordMultiplier = Π over NEW cells on DW/TW of 2 or 3   (DW+DW = ×4, TW+TW = ×9)
 *   score          = letterSum × wordMultiplier
 *
 * A blank contributes 0 to letterSum even on DL/TL, but a DW/TW under a NEW
 * blank still multiplies the whole word.
 */
export function scoreWord(cells: readonly WordCell[]): {
  letterSum: number;
  wordMultiplier: number;
  score: number;
} {
  let letterSum = 0;
  let wordMultiplier = 1;
  for (const cell of cells) {
    const face = cell.blank ? 0 : cell.value;
    if (cell.isNew && cell.premium !== null) {
      // Premium squares only count on the turn a tile is placed on them.
      const { letter, word } = PREMIUM_MULTIPLIER[cell.premium];
      letterSum += face * letter;
      wordMultiplier *= word;
    } else {
      // Existing tiles (and plain squares) score face value.
      letterSum += face;
    }
  }
  return { letterSum, wordMultiplier, score: letterSum * wordMultiplier };
}

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

/** A placed tile after rack lookup: letter and value come from the RACK, never the client. */
interface NewTile {
  row: number;
  col: number;
  tileId: string;
  letter: string;
  value: number;
  blank: boolean;
}

type Check<T> = { ok: true; value: T } | { ok: false; error: MoveError };

const key = (row: number, col: number): string => `${row},${col}`;

/** Deduplicate coordinates, keeping the first occurrence's order. */
function uniqueCoords(cells: readonly Coord[]): Coord[] {
  const seen = new Set<string>();
  const out: Coord[] = [];
  for (const c of cells) {
    const k = key(c.row, c.col);
    if (seen.has(k)) continue;
    seen.add(k);
    out.push({ row: c.row, col: c.col });
  }
  return out;
}

function fail(code: MoveError['code'], message: string, cells?: readonly Coord[]): { ok: false; error: MoveError } {
  const error: MoveError = { code, message };
  if (cells && cells.length > 0) error.cells = uniqueCoords(cells);
  return { ok: false, error };
}

/** Squares of the given placements that are on the board (for highlighting). */
function onBoardCells(placements: readonly Placement[]): Coord[] {
  return placements.filter((p) => inBounds(p.row, p.col)).map((p) => ({ row: p.row, col: p.col }));
}

/**
 * Checks 1–8 of the rule table: tile count, tile identity (against the rack),
 * blank letters, and per-square legality. Resolves each placement to a NewTile.
 */
function resolvePlacements(
  board: Board,
  placements: readonly Placement[],
  rack: readonly Tile[],
): Check<NewTile[]> {
  // 1. NO_TILES
  if (placements.length === 0) return fail('NO_TILES', messages.noTiles());

  // 2. TOO_MANY_TILES
  if (placements.length > RACK_SIZE) {
    return fail('TOO_MANY_TILES', messages.tooManyTiles(placements.length));
  }

  // 3. DUPLICATE_TILE — the same rack tile used on two squares.
  const idCounts = new Map<string, number>();
  for (const p of placements) idCounts.set(p.tileId, (idCounts.get(p.tileId) ?? 0) + 1);
  const duplicated = placements.filter((p) => (idCounts.get(p.tileId) ?? 0) > 1);
  if (duplicated.length > 0) {
    return fail('DUPLICATE_TILE', messages.duplicateTile(), onBoardCells(duplicated));
  }

  // 4. TILE_NOT_IN_RACK — every tile must be one the player actually holds.
  const rackById = new Map<string, Tile>();
  for (const t of rack) rackById.set(t.id, t);
  const missing = placements.filter((p) => !rackById.has(p.tileId));
  if (missing.length > 0) {
    return fail('TILE_NOT_IN_RACK', messages.tileNotInRack(missing.length), onBoardCells(missing));
  }

  // 5. BLANK_NEEDS_LETTER / INVALID_LETTER — first offending placement wins.
  //    Normal tiles: letter and value come from the rack tile; any client letter is ignored.
  //    Blanks: the placement letter is upper-cased and must be exactly one of A–Z.
  const resolved: NewTile[] = [];
  for (const p of placements) {
    const tile = rackById.get(p.tileId) as Tile;
    const at = inBounds(p.row, p.col) ? { row: p.row, col: p.col } : null;
    if (!tile.blank) {
      resolved.push({ row: p.row, col: p.col, tileId: tile.id, letter: tile.letter, value: tile.value, blank: false });
      continue;
    }
    const raw: unknown = p.letter;
    if (raw === undefined || raw === null || raw === '') {
      return fail('BLANK_NEEDS_LETTER', messages.blankNeedsLetter(at), at ? [at] : undefined);
    }
    const letter = typeof raw === 'string' ? raw.toUpperCase() : '';
    if (!/^[A-Z]$/.test(letter)) {
      return fail('INVALID_LETTER', messages.invalidLetter(at), at ? [at] : undefined);
    }
    resolved.push({ row: p.row, col: p.col, tileId: tile.id, letter, value: 0, blank: true });
  }

  // 6. OUT_OF_BOUNDS
  const outside = placements.filter((p) => !inBounds(p.row, p.col));
  if (outside.length > 0) {
    return fail(
      'OUT_OF_BOUNDS',
      messages.outOfBounds(outside.length),
      outside.map((p) => ({ row: p.row, col: p.col })),
    );
  }

  // 7. DUPLICATE_SQUARE — two tiles on one square.
  const squareCounts = new Map<string, number>();
  for (const p of placements) squareCounts.set(key(p.row, p.col), (squareCounts.get(key(p.row, p.col)) ?? 0) + 1);
  const doubled = uniqueCoords(placements.filter((p) => (squareCounts.get(key(p.row, p.col)) ?? 0) > 1));
  if (doubled.length > 0) {
    return fail('DUPLICATE_SQUARE', messages.duplicateSquare(doubled), doubled);
  }

  // 8. SQUARE_OCCUPIED — squares already holding a committed tile.
  const occupied = placements.filter((p) => tileAt(board, p.row, p.col) !== null);
  if (occupied.length > 0) {
    const cells = uniqueCoords(occupied);
    return fail('SQUARE_OCCUPIED', messages.squareOccupied(cells), cells);
  }

  return { ok: true, value: resolved };
}

/** Unit step along a direction. */
function step(direction: Direction): { dr: number; dc: number } {
  return direction === 'H' ? { dr: 0, dc: 1 } : { dr: 1, dc: 0 };
}

/** Position of a square along a direction (column for H, row for V). */
function along(c: Coord, direction: Direction): number {
  return direction === 'H' ? c.col : c.row;
}

/**
 * Checks 9–13: line, gaps, first-move rules and connectivity. Returns the
 * direction of the main word.
 */
function checkGeometry(board: Board, tiles: readonly NewTile[]): Check<Direction> {
  const first = tiles[0] as NewTile;
  const allSameRow = tiles.every((t) => t.row === first.row);
  const allSameCol = tiles.every((t) => t.col === first.col);

  // 9. NOT_IN_LINE
  if (!allSameRow && !allSameCol) {
    return fail('NOT_IN_LINE', messages.notInLine(), tiles);
  }

  // Direction: for 2+ tiles the shared line. For a single tile 'H' when it has
  // a horizontal neighbour, otherwise 'V' (a tile with both neighbours makes an
  // H main word and a V cross-word).
  let direction: Direction;
  if (tiles.length >= 2) {
    direction = allSameRow ? 'H' : 'V';
  } else {
    const hasHorizontalNeighbour =
      tileAt(board, first.row, first.col - 1) !== null || tileAt(board, first.row, first.col + 1) !== null;
    direction = hasHorizontalNeighbour ? 'H' : 'V';
  }

  // 10. HAS_GAPS — from the first to the last placed tile, every square along
  // the line must be filled, either by a new tile or by one already on the board.
  const placed = new Set(tiles.map((t) => key(t.row, t.col)));
  const positions = tiles.map((t) => along(t, direction));
  const lo = Math.min(...positions);
  const hi = Math.max(...positions);
  const gaps: Coord[] = [];
  for (let i = lo; i <= hi; i++) {
    const row = direction === 'H' ? first.row : i;
    const col = direction === 'H' ? i : first.col;
    if (!placed.has(key(row, col)) && tileAt(board, row, col) === null) gaps.push({ row, col });
  }
  if (gaps.length > 0) {
    return fail('HAS_GAPS', messages.hasGaps(gaps), gaps);
  }

  if (isBoardEmpty(board)) {
    // 11. FIRST_MOVE_CENTER
    if (!tiles.some((t) => t.row === CENTER && t.col === CENTER)) {
      return fail('FIRST_MOVE_CENTER', messages.firstMoveCenter(), [{ row: CENTER, col: CENTER }]);
    }
    // 12. FIRST_MOVE_TOO_SHORT
    if (tiles.length < 2) {
      return fail('FIRST_MOVE_TOO_SHORT', messages.firstMoveTooShort(), tiles);
    }
  } else {
    // 13. NOT_CONNECTED — at least one placed tile must be orthogonally
    // adjacent to a tile already on the board. (A placement that spans an
    // existing tile is adjacent to it, so it is connected too.)
    const touches = tiles.some(
      (t) =>
        tileAt(board, t.row - 1, t.col) !== null ||
        tileAt(board, t.row + 1, t.col) !== null ||
        tileAt(board, t.row, t.col - 1) !== null ||
        tileAt(board, t.row, t.col + 1) !== null,
    );
    if (!touches) {
      return fail('NOT_CONNECTED', messages.notConnected(), tiles);
    }
  }

  return { ok: true, value: direction };
}

// ---------------------------------------------------------------------------
// Word extraction
// ---------------------------------------------------------------------------

/**
 * Every word formed by `tiles` (already validated) on `board`: the main word
 * along `direction` first, then each cross-word (perpendicular run of length
 * ≥ 2 through a new tile), ordered along the main line. Words are unscored
 * cell lists; scoring is applied by the caller.
 */
function extractWordCells(
  board: Board,
  tiles: readonly NewTile[],
  direction: Direction,
): { direction: Direction; isMain: boolean; cells: WordCell[] }[] {
  const newAt = new Map<string, NewTile>();
  for (const t of tiles) newAt.set(key(t.row, t.col), t);

  /** The (new or existing) tile on a square as a WordCell, or null if empty. */
  const cellAt = (row: number, col: number): WordCell | null => {
    if (!inBounds(row, col)) return null;
    const fresh = newAt.get(key(row, col));
    if (fresh) {
      return { row, col, letter: fresh.letter, value: fresh.value, blank: fresh.blank, isNew: true, premium: premiumAt(row, col) };
    }
    const existing = tileAt(board, row, col);
    if (existing) {
      return {
        row,
        col,
        letter: existing.letter,
        value: existing.blank ? 0 : existing.value,
        blank: existing.blank,
        isNew: false,
        premium: premiumAt(row, col),
      };
    }
    return null;
  };

  /** The maximal run of filled squares through (row, col) along `dir`, in reading order. */
  const runThrough = (row: number, col: number, dir: Direction): WordCell[] => {
    const { dr, dc } = step(dir);
    let r = row;
    let c = col;
    while (cellAt(r - dr, c - dc) !== null) {
      r -= dr;
      c -= dc;
    }
    const cells: WordCell[] = [];
    for (let cell = cellAt(r, c); cell !== null; cell = cellAt(r, c)) {
      cells.push(cell);
      r += dr;
      c += dc;
    }
    return cells;
  };

  const ordered = tiles.slice().sort((a, b) => along(a, direction) - along(b, direction));
  const anchor = ordered[0] as NewTile;
  const words: { direction: Direction; isMain: boolean; cells: WordCell[] }[] = [
    { direction, isMain: true, cells: runThrough(anchor.row, anchor.col, direction) },
  ];

  const cross: Direction = direction === 'H' ? 'V' : 'H';
  for (const t of ordered) {
    const cells = runThrough(t.row, t.col, cross);
    if (cells.length >= 2) words.push({ direction: cross, isMain: false, cells });
  }
  return words;
}

// ---------------------------------------------------------------------------
// evaluateMove
// ---------------------------------------------------------------------------

/**
 * Validate a placement against the board and the player's rack, extract every
 * formed word (main + cross-words) and score it.
 *
 * `rack` is the placing player's rack: every placement's tileId must be in it.
 * When `dictionary` is supplied every formed word is checked and an
 * INVALID_WORDS error (with `words` and `total`) is returned on failure; when
 * omitted, words carry `valid: undefined` and only geometry is enforced.
 *
 * Check order (first failure wins, so messages are predictable):
 *  NO_TILES → TOO_MANY_TILES → DUPLICATE_TILE → TILE_NOT_IN_RACK →
 *  BLANK_NEEDS_LETTER / INVALID_LETTER → OUT_OF_BOUNDS → DUPLICATE_SQUARE →
 *  SQUARE_OCCUPIED → NOT_IN_LINE → HAS_GAPS → (first move) FIRST_MOVE_CENTER →
 *  FIRST_MOVE_TOO_SHORT → (later moves) NOT_CONNECTED → INVALID_WORDS.
 */
export function evaluateMove(
  board: Board,
  placements: readonly Placement[],
  rack: readonly Tile[],
  dictionary?: Dictionary,
): MoveEvaluation {
  const resolved = resolvePlacements(board, placements, rack);
  if (!resolved.ok) return resolved;
  const tiles = resolved.value;

  const geometry = checkGeometry(board, tiles);
  if (!geometry.ok) return geometry;
  const direction = geometry.value;

  const words: FormedWord[] = extractWordCells(board, tiles, direction).map(({ direction: dir, isMain, cells }) => {
    const { letterSum, wordMultiplier, score } = scoreWord(cells);
    const formed: FormedWord = {
      word: cells.map((c) => c.letter).join(''),
      direction: dir,
      cells,
      letterSum,
      wordMultiplier,
      score,
      isMain,
    };
    if (dictionary) formed.valid = dictionary.has(formed.word);
    return formed;
  });

  const wordsScore = words.reduce((sum, w) => sum + w.score, 0);
  // Bingo: every one of the RACK_SIZE rack tiles played in a single move.
  const bingo = tiles.length === RACK_SIZE;
  const bingoBonus = bingo ? BINGO_BONUS : 0;
  const total = wordsScore + bingoBonus;

  // 14. INVALID_WORDS — only when a dictionary was supplied.
  if (dictionary) {
    const invalid = words.filter((w) => w.valid === false);
    if (invalid.length > 0) {
      const invalidWords = [...new Set(invalid.map((w) => w.word))];
      const error: MoveError = {
        code: 'INVALID_WORDS',
        message: messages.invalidWords(invalidWords),
        invalidWords,
        cells: uniqueCoords(invalid.flatMap((w) => w.cells)),
      };
      return { ok: false, error, words, total };
    }
  }

  return { ok: true, direction, words, wordsScore, bingo, bingoBonus, total, tilesUsed: tiles.length };
}

// ---------------------------------------------------------------------------
// Hints
// ---------------------------------------------------------------------------

/**
 * Soft placement hints for the UI (never enforced): with no placements, every
 * empty square orthogonally adjacent to a tile; with placements, the empty
 * squares on the line(s) the next tile could legally extend (one tile → its
 * row and column; two+ in a line → that line).
 *
 * First move (empty board): the word must cover the centre, so a tile can only
 * start on the centre row or column, within RACK_SIZE − 1 squares of it; and
 * once tiles are drafted, only a line through the centre is hinted. Drafts in
 * a line that misses the centre point at the centre alone.
 *
 * "Empty" means neither a committed tile nor a draft placement. Off-board
 * placements are ignored; placements that are not in one line yield no hints.
 * The result is deduplicated and in row-major order.
 */
export function hintCells(board: Board, placements: readonly Placement[]): Coord[] {
  const drafts = placements.filter((p) => inBounds(p.row, p.col));
  const drafted = new Set(drafts.map((p) => key(p.row, p.col)));
  const isEmpty = (row: number, col: number): boolean =>
    inBounds(row, col) && tileAt(board, row, col) === null && !drafted.has(key(row, col));

  const wanted = new Set<string>();

  const firstMove = isBoardEmpty(board);

  if (drafts.length === 0 && firstMove) {
    // Any square a first word through the centre can reach.
    for (let i = CENTER - (RACK_SIZE - 1); i <= CENTER + (RACK_SIZE - 1); i++) {
      if (isEmpty(CENTER, i)) wanted.add(key(CENTER, i));
      if (isEmpty(i, CENTER)) wanted.add(key(i, CENTER));
    }
  } else if (drafts.length === 0) {
    for (let row = 0; row < BOARD_SIZE; row++) {
      for (let col = 0; col < BOARD_SIZE; col++) {
        if (tileAt(board, row, col) === null) continue;
        for (const [r, c] of [
          [row - 1, col],
          [row + 1, col],
          [row, col - 1],
          [row, col + 1],
        ] as const) {
          if (isEmpty(r, c)) wanted.add(key(r, c));
        }
      }
    }
  } else {
    const first = drafts[0] as Placement;
    const sameRow = drafts.every((p) => p.row === first.row);
    const sameCol = drafts.every((p) => p.col === first.col);
    const addRow = (row: number): void => {
      for (let col = 0; col < BOARD_SIZE; col++) if (isEmpty(row, col)) wanted.add(key(row, col));
    };
    const addCol = (col: number): void => {
      for (let row = 0; row < BOARD_SIZE; row++) if (isEmpty(row, col)) wanted.add(key(row, col));
    };
    // A single tile (or several stacked on one square) could still grow either
    // way. On the first move only a line through the centre can be completed.
    const growRow = sameRow && (!firstMove || first.row === CENTER);
    const growCol = sameCol && (!firstMove || first.col === CENTER);
    if (growRow) addRow(first.row);
    if (growCol) addCol(first.col);
    if (firstMove && (sameRow || sameCol) && !growRow && !growCol) wanted.add(key(CENTER, CENTER));
  }

  const out: Coord[] = [];
  for (let row = 0; row < BOARD_SIZE; row++) {
    for (let col = 0; col < BOARD_SIZE; col++) {
      if (wanted.has(key(row, col))) out.push({ row, col });
    }
  }
  return out;
}
